import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ExportButtons from './components/ExportButtons';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import HelpButton from '../../help/HelpButton';

function fmtSecs(n) {
  if (n == null) return '—';
  const s = Number(n) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}


export default function QueuePerformance() {
  const [range, setRange, apiParams] = useRangeState('today');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'queues', merged],
    queryFn: () => reportsApi.queues(merged),
    refetchInterval: range.preset === 'today' ? 30_000 : false,
    keepPreviousData: true,
  });

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['queue']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">{data?.title || 'Queue performance'}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="queues" params={merged} disabled={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-300 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {data?.meta && (
        <div className="text-xs text-surface-muted mb-2 flex flex-wrap gap-x-3">
          {data.meta.counters_source && <span>Counters: <strong>{data.meta.counters_source}</strong></span>}
          {data.meta.talk_source && <span>Talk time: <strong>{data.meta.talk_source}</strong></span>}
          {data.meta.latest_datapoint && <span>Latest CDR: <strong>{data.meta.latest_datapoint}</strong></span>}
          {data.meta.failed_clusters?.length > 0 && (
            <span className="text-accent-red">⚠ partial: failed on {data.meta.failed_clusters.map(f=>f.cluster).join(', ')}</span>
          )}
        </div>
      )}

      <ReportTable
        // 2026-08-10: the backend now computes pct_answered / pct_abandoned /
        // service_level / asa server-side (single source of truth = matches
        // the CSV/PDF export exactly). We render the server headers directly
        // instead of appending a browser-only pct_answered column.
        headers={
          data?.headers
            ? data.headers
            : ['queue_id','queue_name','offered','calls_answered','calls_abandoned','calls_missed','pct_abandoned','service_level','transfers_warm','transfers_cold','calls_transferred','calls_hold_events','asa_sec','max_queue_sec','total_talk_sec','avg_talk_sec','avg_setup_sec','_clusters']
        }
        rows={data?.rows || []}
        columnLabels={{
          queue_id: 'Queue ID',
          queue_name: 'Queue name',
          offered: 'Offered',
          calls_answered: 'Answered',
          calls_abandoned: 'Abandoned',
          calls_missed: 'Missed',
          pct_answered: 'Answer %',
          pct_abandoned: 'Abandon %',
          service_level: 'Service Level %',
          transfers_warm: 'Warm xfer',
          transfers_cold: 'Cold xfer',
          calls_transferred: 'Transfers',
          calls_hold_events: 'Holds',
          avg_queue_sec: 'Avg queue',
          asa_sec: 'ASA',
          max_queue_sec: 'Max Delay',
          total_talk_sec: 'Total talk',
          avg_talk_sec: 'Inbound AHT',
          avg_setup_sec: 'Avg setup',
          _clusters: 'DC',
        }}
        formatters={{
          total_talk_sec: fmtSecs,
          avg_talk_sec: fmtSecs,
          avg_setup_sec: fmtSecs,
          avg_queue_sec: fmtSecs,
          asa_sec: fmtSecs,
          max_queue_sec: fmtSecs,
          pct_answered: (v) => (v == null ? '—' : `${v}%`),
          pct_abandoned: (v) => (v == null ? '—' : `${v}%`),
          service_level: (v) => (v == null ? '—' : `${v}%`),
        }}
      />
    </div>
  );
}
